import React, { useMemo, useState } from "react";

import { ModifierSetDto } from "services/modifierSets/restModels";
import { ModifierSetRequestDto } from "services/modifierSets/restRequests";
import { AddIcon, Button, EmptyState, OrderButtons, SectionCard } from "shared/ui";

import { AddModifierSetDialog } from "features/MenuItemEditor/components/AddModifierSetDialog";
import { useMenuItemEditorContext } from "features/MenuItemEditor/context";
import { ModifierSetDialog } from "features/ModifierSets/components/ModifierSetDialog";
import { modifierSetSummary } from "features/ModifierSets/lib/modifierSetSummary";
import { localizedText } from "shared/lib/localizedMap";

import {
    Header,
    HeaderText,
    Info,
    List,
    Loading,
    Meta,
    MissingMeta,
    Name,
    Row,
    RowActions,
} from "./styles";

export const ModifiersTab = () => {
    const state = useMenuItemEditorContext();

    const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
    const [isSetDialogOpen, setIsSetDialogOpen] = useState<boolean>(false);
    const [editedModifierSet, setEditedModifierSet] = useState<ModifierSetDto | null>(null);

    /*
    * Внешний товар берём из выбранного в основной вкладке, а если её ещё не открывали
    * (заход по прямой ссылке ?tab=modifiers) — из сохранённой привязки позиции.
    * */
    const externalProductId = state.selectedExternalProduct
        ? state.selectedExternalProduct.id
        : state.externalId;

    const externalProductOptionsContext =
        state.hasIikoIntegration && externalProductId
            ? { menuId: state.menuId, externalProductId }
            : null;

    // Каталог перебирается на каждую строку списка, а стейт редактора пересоздаётся
    // на каждый ввод — держим индекс, а не линейный поиск.
    const catalogById = useMemo(() => {
        const index: Record<number, ModifierSetDto> = {};
        state.modifierSetsCatalog.forEach((set) => {
            index[set.id] = set;
        });
        return index;
    }, [state.modifierSetsCatalog]);

    const availableModifierSets = useMemo(() => {
        const attached = state.modifierSets.map((ref) => ref.menuItemModifierSetId);
        return state.modifierSetsCatalog.filter((set) => attached.indexOf(set.id) === -1);
    }, [state.modifierSetsCatalog, state.modifierSets]);

    const openCreateDialog = () => {
        setEditedModifierSet(null);
        setIsAddDialogOpen(false);
        setIsSetDialogOpen(true);
    };

    const openEditDialog = (modifierSet: ModifierSetDto) => {
        setEditedModifierSet(modifierSet);
        setIsSetDialogOpen(true);
    };

    const closeSetDialog = () => {
        setIsSetDialogOpen(false);
        setEditedModifierSet(null);
    };

    const handleAdd = (modifierSetId: number) => {
        state.addModifierSet(modifierSetId);
        setIsAddDialogOpen(false);
    };

    const handleSubmitSet = async (values: ModifierSetRequestDto): Promise<boolean> => {
        const saved = await state.saveModifierSet(
            values,
            editedModifierSet ? editedModifierSet.id : null
        );

        if (!saved) return false;

        // Набор создавали из позиции — сразу его и подключаем.
        if (!editedModifierSet) state.addModifierSet(saved.id);

        closeSetDialog();
        return true;
    };

    return (
        <>
            <Header>
                <HeaderText>
                    Наборы модификаторов — размеры, добавки и комбо. Порядок наборов определяет
                    порядок показа в карточке. Сам набор может быть общим для нескольких позиций:
                    правки отразятся во всех, к которым он привязан.
                </HeaderText>
                <Button variant="contained" onClick={() => setIsAddDialogOpen(true)}>
                    <AddIcon fontSize="small" /> Добавить набор
                </Button>
            </Header>

            <SectionCard>
                {state.modifierSetsLoading ? (
                    <Loading>Загрузка наборов…</Loading>
                ) : state.modifierSets.length === 0 ? (
                    <EmptyState
                        title={
                            <>
                                Добавьте набор модификаторов для блюда{" "}
                                <span role="img" aria-label="cherry">
                                    🍒
                                </span>
                            </>
                        }
                        description="Размеры, добавки и комбо — всё через наборы модификаторов."
                        action={
                            <Button variant="contained" onClick={() => setIsAddDialogOpen(true)}>
                                <AddIcon fontSize="small" /> Добавить набор
                            </Button>
                        }
                    />
                ) : (
                    <List>
                        {state.modifierSets.map((ref, index) => {
                            const modifierSet = catalogById[ref.menuItemModifierSetId] || null;

                            return (
                                <Row key={`${ref.menuItemModifierSetId}_${index}`}>
                                    <OrderButtons
                                        onUp={() => state.moveModifierSet(index, "up")}
                                        onDown={() => state.moveModifierSet(index, "down")}
                                        disableUp={index === 0}
                                        disableDown={index === state.modifierSets.length - 1}
                                    />
                                    <Info>
                                        <Name>
                                            {modifierSet
                                                ? localizedText(modifierSet.name, state.locale)
                                                : `Набор #${ref.menuItemModifierSetId}`}
                                        </Name>
                                        {modifierSet ? (
                                            <Meta>{modifierSetSummary(modifierSet)}</Meta>
                                        ) : (
                                            <MissingMeta>
                                                Набор удалён — отвяжите его от позиции
                                            </MissingMeta>
                                        )}
                                    </Info>
                                    <RowActions>
                                        {modifierSet && (
                                            <Button
                                                variant="text"
                                                onClick={() => openEditDialog(modifierSet)}
                                            >
                                                Изменить
                                            </Button>
                                        )}
                                        <Button
                                            variant="text"
                                            color="danger"
                                            onClick={() => state.removeModifierSet(index)}
                                        >
                                            Отвязать
                                        </Button>
                                    </RowActions>
                                </Row>
                            );
                        })}
                    </List>
                )}
            </SectionCard>

            <AddModifierSetDialog
                open={isAddDialogOpen}
                availableModifierSets={availableModifierSets}
                locale={state.locale}
                onClose={() => setIsAddDialogOpen(false)}
                onAdd={handleAdd}
                onCreateNew={openCreateDialog}
            />

            <ModifierSetDialog
                open={isSetDialogOpen}
                modifierSet={editedModifierSet}
                locale={state.locale}
                availableLanguages={state.availableLanguages}
                currencyFormatter={state.currencyFormatter}
                externalIntegrationEnabled={state.hasIikoIntegration}
                externalProductOptionsContext={externalProductOptionsContext}
                saving={state.savingModifierSet}
                onClose={closeSetDialog}
                onSubmit={handleSubmitSet}
            />
        </>
    );
};
